/*
 * Throwaway preview — shows three competing layouts for the new
 * "Wire & ACH instructions" card the user asked about. Lets them
 * visually compare Mercury-style tabs vs Stripe-style side-by-side
 * vs Brex-style single stack, then approve which to ship.
 *
 * Hit /preview/bank-instructions. Pure mockup — no real data.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, Copy, Eye, EyeOff, Download, Mail, Lock,
  CheckCircle2, Clock, Globe, ChevronRight, ArrowRight, Sparkles,
} from "lucide-react";

/* ── Mock data — what we'd render from a real payment_method row ───── */
const MOCK_METHOD = {
  bank_name: "Chase Business Bank",
  bank_address_line1: "270 Park Avenue",
  bank_address_line2: null,
  bank_city: "New York",
  bank_state: "NY",
  bank_postal_code: "10017",
  bank_country: "US",
  account_holder: "Acme Trading LLC",
  account_holder_address_line1: "500 Folsom Street, Suite 200",
  account_holder_address_line2: null,
  account_holder_city: "San Francisco",
  account_holder_state: "CA",
  account_holder_postal_code: "94105",
  account_holder_country: "US",
  routing_number_last4: "6791",         // ACH routing
  wire_routing_number_last4: "0021",    // FedWire ABA
  account_number_last4: "2847",
  swift_bic: "CHASUS33",
  iban: null,
  intermediary_bank_name: null,
  currency: "USD",
  rails: "ach,wire,intl_wire",
  last4: "2847",
};

const OPTIONS = [
  { key: "tabbed",  label: "A · Tabbed",   sub: "Mercury-style",          rec: "Cleanest" },
  { key: "split",   label: "B · Side-by-side", sub: "Stripe-style",       rec: "Best detail density" },
  { key: "stacked", label: "C · Single card", sub: "Brex-style",          rec: "Most compact" },
];

const SCOPE_OPTIONS = [
  { key: "minimal",    label: "Minimal — card only, no PDF" },
  { key: "full",       label: "Full — card + PDF download" },
  { key: "full-admin", label: "Full + admin lender wallets" },
];

const PDF_OPTIONS = [
  { key: "masked",    label: "Masked default → full after re-auth", rec: "Mercury · recommended" },
  { key: "alwaysfull",label: "Full numbers always on PDF" },
  { key: "nofull",    label: "Numbers never on PDF (addresses only)" },
];

export default function BankInstructionsPreview() {
  const [pick, setPick]       = useState("split");
  const [scope, setScope]     = useState("full");
  const [pdfPol, setPdfPol]   = useState("masked");

  return (
    <div className="min-h-screen bg-secondary/30">
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-[1240px] mx-auto px-8 py-4 flex items-center gap-6 flex-wrap">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/50">Wallet · pre-approval preview</div>
            <div className="display text-lg mt-0.5">Wire & ACH Instructions — pick a layout</div>
          </div>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 rounded-full border border-border bg-white p-1">
              {OPTIONS.map(o => (
                <button key={o.key}
                  data-testid={`bank-instr-pick-${o.key}`}
                  onClick={() => setPick(o.key)}
                  className={`rounded-full px-4 h-9 text-[12px] mono uppercase tracking-[0.12em] transition-colors flex items-center gap-2 ${
                    pick === o.key ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"
                  }`}>
                  {o.label}
                  {o.rec && pick === o.key && <span className="chip bg-accent/30 border-accent/40 text-[9px]">{o.rec}</span>}
                </button>
              ))}
            </div>
            <Link to="/" className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/50 hover:text-foreground">← Back</Link>
          </div>
        </div>
      </div>

      <div className="max-w-[1240px] mx-auto px-8 py-10 space-y-10">
        {/* Hero — the why */}
        <Hero/>

        {/* Live preview */}
        <section>
          <SectionHeader
            n="01"
            title="The live preview"
            sub={`Toggle the tabs above to swap between layouts. Sensitive numbers are reveal-gated in all three. Pretend you're a seller about to share these details with a buyer's AP team.`}
          />
          <div className="mt-5">
            {pick === "tabbed"  && <OptionTabbed/>}
            {pick === "split"   && <OptionSplit/>}
            {pick === "stacked" && <OptionStacked/>}
          </div>

          <ProsCons pick={pick}/>
        </section>

        {/* Research */}
        <section>
          <SectionHeader
            n="02"
            title="What the leaders do"
            sub="The pattern we converged on — pulled from Mercury, Stripe Treasury, Brex, Wise, Chase Business & Modern Treasury."
          />
          <ResearchTable/>
        </section>

        {/* Gap analysis */}
        <section>
          <SectionHeader
            n="03"
            title="What Verqo already has vs what's missing"
            sub="The good news: most data is already collected and persisted. We're missing the rendering layer."
          />
          <GapTable/>
        </section>

        {/* Choices */}
        <section>
          <SectionHeader
            n="04"
            title="What I'd like you to pick"
            sub="Three decisions, then I'll ship it."
          />
          <div className="mt-5 grid md:grid-cols-3 gap-4">
            <ChoiceCard
              title="Layout" testidRoot="choice-layout"
              picks={OPTIONS.map(o => ({ key: o.key, label: o.label, sub: o.sub, rec: o.rec }))}
              value={pick} onChange={setPick}
            />
            <ChoiceCard
              title="Scope" testidRoot="choice-scope"
              picks={SCOPE_OPTIONS.map(s => ({ key: s.key, label: s.label }))}
              value={scope} onChange={setScope}
            />
            <ChoiceCard
              title="PDF sensitive-number policy" testidRoot="choice-pdf"
              picks={PDF_OPTIONS.map(p => ({ key: p.key, label: p.label, rec: p.rec }))}
              value={pdfPol} onChange={setPdfPol}
            />
          </div>

          <div className="mt-6 rounded-2xl bg-foreground text-background p-6">
            <div className="text-[10px] mono uppercase tracking-[0.22em] text-background/55">Your choices</div>
            <div className="display text-2xl mt-2 tracking-tight">
              <span className="text-background/80">Layout</span> <strong>{labelOf(OPTIONS, pick)}</strong>
              <span className="text-background/40 px-2">·</span>
              <span className="text-background/80">Scope</span> <strong>{labelOf(SCOPE_OPTIONS, scope)}</strong>
              <span className="text-background/40 px-2">·</span>
              <span className="text-background/80">PDF</span> <strong>{labelOf(PDF_OPTIONS, pdfPol)}</strong>
            </div>
            <p className="text-background/65 text-sm mt-3 max-w-2xl">
              Reply to me in chat with these three picks (or just say "ship it with your recommendations" — I'll default to <strong>B · split + Full + Masked-then-reveal</strong>) and I'll execute the plan in the doc at <span className="mono text-background/85">/app/memory/BANK_INSTRUCTIONS_RESEARCH.md</span>.
            </p>
          </div>
        </section>

        {/* Plan */}
        <section>
          <SectionHeader
            n="05"
            title="The build (file-by-file)"
            sub="Estimate ~1h 40m, low risk, backwards-compatible. No 3rd-party integration needed."
          />
          <PlanGrid/>
        </section>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/*  Reusable bits                                                      */
/* ─────────────────────────────────────────────────────────────────── */

function Hero() {
  return (
    <div className="rounded-3xl bg-gradient-to-br from-foreground to-foreground/85 text-background p-10 overflow-hidden relative">
      <Sparkles className="w-72 h-72 absolute -right-10 -bottom-20 text-background/5" strokeWidth={0.5}/>
      <div className="text-[10px] mono uppercase tracking-[0.22em] text-background/55">The ask</div>
      <h1 className="display text-4xl md:text-5xl mt-3 tracking-[-0.02em] leading-tight max-w-3xl">
        Give sellers, buyers, lenders & Allianz a clean way to share their bank instructions —
        without leaking sensitive numbers.
      </h1>
      <p className="text-background/65 text-base mt-4 max-w-2xl leading-relaxed">
        Today we collect bank name, bank address, beneficiary name, beneficiary address, and the reveal-gated routing + account numbers — but only the masked numbers are rendered. Counterparties asking <em>"where do I send the wire?"</em> have no answer in-app.
      </p>
      <div className="flex flex-wrap gap-2 mt-6">
        <Pill icon={Building2}>Bank name + full address</Pill>
        <Pill icon={Globe}>ACH · Wire · Intl Wire</Pill>
        <Pill icon={Lock}>Reveal-gated numbers (already shipped)</Pill>
        <Pill icon={Download}>Downloadable one-pager PDF</Pill>
        <Pill icon={Copy}>Copy buttons on every row</Pill>
      </div>
    </div>
  );
}

function SectionHeader({ n, title, sub }) {
  return (
    <div>
      <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/45">Section {n}</div>
      <h2 className="display text-2xl md:text-3xl mt-1.5 tracking-tight">{title}</h2>
      {sub && <p className="text-foreground/55 mt-2 max-w-2xl text-sm leading-relaxed">{sub}</p>}
    </div>
  );
}

function Pill({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-background/10 border border-background/15 px-3 py-1.5 text-[12.5px] text-background/85">
      <Icon className="w-3.5 h-3.5"/>{children}
    </span>
  );
}

function ChoiceCard({ title, picks, value, onChange, testidRoot }) {
  return (
    <div className="rounded-2xl bg-white border border-border p-5">
      <div className="text-[10.5px] mono uppercase tracking-[0.2em] text-foreground/55">{title}</div>
      <div className="space-y-2 mt-3">
        {picks.map(p => {
          const active = value === p.key;
          return (
            <button key={p.key}
              data-testid={`${testidRoot}-${p.key}`}
              onClick={() => onChange(p.key)}
              className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                active ? "border-foreground bg-foreground/[0.03]" : "border-border hover:border-foreground/30"
              }`}>
              <div className={`w-4 h-4 rounded-full border-2 mt-0.5 shrink-0 grid place-items-center ${active ? "border-foreground" : "border-foreground/30"}`}>
                {active && <div className="w-2 h-2 rounded-full bg-foreground"/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{p.label}</div>
                {p.sub && <div className="text-[11.5px] text-foreground/55">{p.sub}</div>}
                {p.rec && active && <div className="text-[10.5px] mt-1 inline-block chip bg-accent/30 border-accent/40">{p.rec}</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function labelOf(arr, key) { return (arr.find(x => x.key === key) || {}).label || key; }

/* ─────────────────────────────────────────────────────────────────── */
/*  OPTION A · Tabbed                                                  */
/* ─────────────────────────────────────────────────────────────────── */
function OptionTabbed() {
  const [tab, setTab] = useState("ach");
  return (
    <Frame caption="Option A · Tabbed (Mercury-style)">
      <BankHeader/>
      <div className="border-b border-border px-6 -mx-6 mt-4">
        <div className="flex gap-1.5">
          {[
            { k: "ach",  l: "ACH",            sub: "0–3 business days, no fee" },
            { k: "wire", l: "Domestic Wire",  sub: "Same-day before 5pm ET" },
            { k: "intl", l: "International",  sub: "1–3 business days" },
          ].map(t => (
            <button key={t.k}
              data-testid={`bank-instr-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`pb-3 pt-2 px-3 text-[12.5px] border-b-2 transition-colors ${
                tab === t.k
                  ? "border-foreground text-foreground"
                  : "border-transparent text-foreground/55 hover:text-foreground"
              }`}>
              <div className="font-medium">{t.l}</div>
              <div className="text-[10.5px] text-foreground/45 mono mt-0.5">{t.sub}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="pt-5">
        {tab === "ach"  && <AchPanel/>}
        {tab === "wire" && <WirePanel/>}
        {tab === "intl" && <IntlPanel/>}
      </div>
      <Footer/>
    </Frame>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/*  OPTION B · Split (side-by-side)                                    */
/* ─────────────────────────────────────────────────────────────────── */
function OptionSplit() {
  return (
    <Frame caption="Option B · Side-by-side (Stripe-style)">
      <BankHeader/>
      <div className="grid lg:grid-cols-2 gap-4 mt-5">
        <PanelCard title="ACH Transfer" eta="0–3 business days · no fee">
          <AchPanel compact/>
        </PanelCard>
        <PanelCard title="Wire Transfer" eta="Same-day before 5pm ET">
          <WirePanel compact/>
        </PanelCard>
      </div>
      <div className="mt-4">
        <PanelCard title="International Wire" eta="1–3 business days · SWIFT" defaultOpen={false}>
          <IntlPanel compact/>
        </PanelCard>
      </div>
      <Footer/>
    </Frame>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/*  OPTION C · Stacked                                                 */
/* ─────────────────────────────────────────────────────────────────── */
function OptionStacked() {
  return (
    <Frame caption="Option C · Single card stacked (Brex-style)">
      <BankHeader/>
      <div className="mt-5 divide-y divide-border">
        <div className="py-5">
          <SectionLabel icon={ArrowRight}>ACH Transfer · 0–3 business days</SectionLabel>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label="Beneficiary name"           value={MOCK_METHOD.account_holder} canCopy/>
            <Field label="Beneficiary address"        value={addrLine(MOCK_METHOD, "account_holder")} canCopy multiline/>
            <Field label="Bank name"                  value={MOCK_METHOD.bank_name} canCopy/>
            <Field label="Bank address"               value={addrLine(MOCK_METHOD, "bank")} canCopy multiline/>
            <FieldMasked label="ACH routing #"        last4={MOCK_METHOD.routing_number_last4}/>
            <FieldMasked label="Account #"            last4={MOCK_METHOD.account_number_last4}/>
          </div>
        </div>
        <div className="py-5">
          <SectionLabel icon={ArrowRight}>Domestic Wire · same-day before 5pm ET</SectionLabel>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label="Wire routing # · ABA"       value={`••••${MOCK_METHOD.wire_routing_number_last4}`} mono canCopy={false}/>
            <Field label="SWIFT/BIC"                  value={MOCK_METHOD.swift_bic} mono canCopy/>
          </div>
        </div>
      </div>
      <Footer/>
    </Frame>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/*  Shared sub-bits                                                    */
/* ─────────────────────────────────────────────────────────────────── */
function Frame({ caption, children }) {
  return (
    <div>
      <div className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/45 mb-2">{caption}</div>
      <div className="rounded-2xl bg-white border border-border p-6 max-w-5xl">
        {children}
      </div>
    </div>
  );
}

function BankHeader() {
  return (
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-2xl bg-secondary grid place-items-center shrink-0">
        <Building2 className="w-5 h-5"/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="display text-xl tracking-tight">{MOCK_METHOD.bank_name}</div>
        <div className="text-[11.5px] text-foreground/55 mono mt-0.5">
          ···· {MOCK_METHOD.last4} · {MOCK_METHOD.currency} · ACH, Wire, International
        </div>
      </div>
      <span className="chip bg-emerald-50 border-emerald-200 text-emerald-800 text-[10.5px] inline-flex items-center gap-1">
        <CheckCircle2 className="w-2.5 h-2.5"/>Verified
      </span>
    </div>
  );
}

function PanelCard({ title, eta, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl border border-border bg-white">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 p-4 border-b border-border">
        <div className="flex-1 text-left">
          <div className="font-medium text-[14px]">{title}</div>
          <div className="text-[11px] text-foreground/55 mt-0.5">{eta}</div>
        </div>
        <ChevronRight className={`w-4 h-4 text-foreground/45 transition-transform ${open ? "rotate-90" : ""}`}/>
      </button>
      {open && <div className="p-4">{children}</div>}
    </div>
  );
}

function AchPanel({ compact }) {
  return (
    <div className={`grid grid-cols-1 ${compact ? "" : "sm:grid-cols-2"} gap-2`}>
      <Field label="Beneficiary name"    value={MOCK_METHOD.account_holder} canCopy/>
      <Field label="Beneficiary address" value={addrLine(MOCK_METHOD, "account_holder")} canCopy multiline/>
      <Field label="Bank name"           value={MOCK_METHOD.bank_name} canCopy/>
      <Field label="Bank address"        value={addrLine(MOCK_METHOD, "bank")} canCopy multiline/>
      <FieldMasked label="ACH routing #" last4={MOCK_METHOD.routing_number_last4}/>
      <FieldMasked label="Account #"     last4={MOCK_METHOD.account_number_last4}/>
      <Field label="Account type"        value="Checking" canCopy={false}/>
      <Field label="Currency"            value={MOCK_METHOD.currency} canCopy={false}/>
      <div className={`${compact ? "" : "col-span-2"} pt-2 flex gap-2 flex-wrap`}>
        <BtnSecondary icon={Copy}>Copy all ACH instructions</BtnSecondary>
      </div>
    </div>
  );
}

function WirePanel({ compact }) {
  return (
    <div className={`grid grid-cols-1 ${compact ? "" : "sm:grid-cols-2"} gap-2`}>
      <Field label="Beneficiary name"    value={MOCK_METHOD.account_holder} canCopy/>
      <Field label="Beneficiary address" value={addrLine(MOCK_METHOD, "account_holder")} canCopy multiline/>
      <Field label="Bank name"           value={MOCK_METHOD.bank_name} canCopy/>
      <Field label="Bank address"        value={addrLine(MOCK_METHOD, "bank")} canCopy multiline/>
      <FieldMasked label="Wire routing # (ABA)" last4={MOCK_METHOD.wire_routing_number_last4}/>
      <FieldMasked label="Account #"     last4={MOCK_METHOD.account_number_last4}/>
      <Field label="SWIFT/BIC"           value={MOCK_METHOD.swift_bic} mono canCopy/>
      <Field label="Currency"            value={MOCK_METHOD.currency} canCopy={false}/>
      <div className={`${compact ? "" : "col-span-2"} pt-2 flex gap-2 flex-wrap`}>
        <BtnSecondary icon={Copy}>Copy all Wire instructions</BtnSecondary>
      </div>
    </div>
  );
}

function IntlPanel({ compact }) {
  return (
    <div className={`grid grid-cols-1 ${compact ? "" : "sm:grid-cols-2"} gap-2`}>
      <Field label="Beneficiary name"    value={MOCK_METHOD.account_holder} canCopy/>
      <Field label="Beneficiary address" value={addrLine(MOCK_METHOD, "account_holder")} canCopy multiline/>
      <Field label="Bank name"           value={MOCK_METHOD.bank_name} canCopy/>
      <Field label="Bank address"        value={addrLine(MOCK_METHOD, "bank")} canCopy multiline/>
      <Field label="SWIFT/BIC"           value={MOCK_METHOD.swift_bic} mono canCopy/>
      <FieldMasked label="IBAN"          last4="2847" hint="Reveal to copy"/>
      <Field label="Intermediary bank"   value="— not required for USD wires" canCopy={false}/>
      <Field label="Purpose of payment"  value="Invoice settlement" canCopy/>
    </div>
  );
}

function SectionLabel({ icon: Icon, children }) {
  return (
    <div className="text-[10.5px] mono uppercase tracking-[0.2em] text-foreground/55 inline-flex items-center gap-2">
      <Icon className="w-3 h-3"/>{children}
    </div>
  );
}

/* Per-field row with a copy button — non-sensitive (always clear). */
function Field({ label, value, canCopy = true, mono = false, multiline = false }) {
  return (
    <div className="rounded-xl border border-border bg-secondary/30 px-3 py-2">
      <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">{label}</div>
      <div className="flex items-start gap-2 mt-1">
        <div className={`flex-1 min-w-0 text-[13px] ${mono ? "mono" : ""} ${multiline ? "whitespace-pre-line" : "truncate"}`}>
          {value || <span className="text-foreground/40">—</span>}
        </div>
        {canCopy && value && (
          <button className="text-foreground/45 hover:text-foreground transition-colors shrink-0" title="Copy">
            <Copy className="w-3.5 h-3.5"/>
          </button>
        )}
      </div>
    </div>
  );
}

/* Masked field with a Reveal CTA (no real reveal in the preview). */
function FieldMasked({ label, last4, hint }) {
  return (
    <div className="rounded-xl border border-border bg-secondary/30 px-3 py-2">
      <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">{label}</div>
      <div className="flex items-center gap-2 mt-1">
        <code className="flex-1 mono text-[13px] tracking-widest text-foreground/65">{`••••••••${last4}`}</code>
        <button className="inline-flex items-center gap-1 text-[11px] text-foreground/60 hover:text-foreground transition-colors shrink-0">
          <Eye className="w-3.5 h-3.5"/>Show
        </button>
      </div>
      {hint && <div className="text-[10px] text-foreground/45 mt-0.5">{hint}</div>}
    </div>
  );
}

function BtnSecondary({ icon: Icon, children }) {
  return (
    <button className="inline-flex items-center gap-2 rounded-full bg-foreground/[0.05] hover:bg-foreground/10 border border-foreground/15 px-3.5 h-9 text-[12.5px]">
      <Icon className="w-3.5 h-3.5"/>{children}
    </button>
  );
}

function Footer() {
  return (
    <div className="mt-5 pt-5 border-t border-border flex items-center justify-between flex-wrap gap-3">
      <div className="text-[11px] text-foreground/55 flex items-center gap-2">
        <Clock className="w-3.5 h-3.5"/>
        ACH 0–3 business days · Domestic wire same-day before 5pm ET · International 1–3 business days
      </div>
      <div className="flex gap-2 flex-wrap">
        <BtnSecondary icon={Download}>Download PDF</BtnSecondary>
        <BtnSecondary icon={Mail}>Email instructions</BtnSecondary>
      </div>
    </div>
  );
}

function addrLine(m, prefix) {
  const a1 = m[`${prefix}_address_line1`];
  const a2 = m[`${prefix}_address_line2`];
  const city  = m[`${prefix}_city`];
  const state = m[`${prefix}_state`];
  const zip   = m[`${prefix}_postal_code`];
  const country = m[`${prefix}_country`];
  return [a1, a2, [city, state, zip].filter(Boolean).join(", "), country].filter(Boolean).join("\n");
}

/* ─── Pros & Cons + research tables ──────────────────────────────── */
function ProsCons({ pick }) {
  const c = {
    tabbed: {
      pros: ["Clean — only one rail's data visible at a time", "Closely mirrors Mercury's pattern most users know", "Easy to add more rails later (RTP, FedNow, etc.)"],
      cons: ["Slight friction — extra click to compare ACH vs wire", "Tab labels eat a row of vertical space"],
    },
    split: {
      pros: ["Both rails visible at once — fastest 'send this to my customer' flow", "Best for desktop screens (Verqo's main use-case)", "Stripe's exact pattern — proven in B2B SaaS"],
      cons: ["Tight on tablet width (~768px) — falls back to stacked", "International wire gets pushed below the fold"],
    },
    stacked: {
      pros: ["Most scannable — one continuous list", "Mobile-friendly out of the box", "Lowest cognitive load for users with one rail"],
      cons: ["Long page for users with all 3 rails configured", "Visual hierarchy weaker — less obvious which fields belong to ACH vs wire"],
    },
  }[pick];

  return (
    <div className="mt-5 rounded-2xl bg-white border border-border p-6 max-w-5xl">
      <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">
        {pick === "tabbed" ? "Tabbed" : pick === "split" ? "Side-by-side" : "Stacked"} · pros & cons
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-6">
        <div>
          <div className="text-[11px] mono uppercase tracking-[0.18em] text-emerald-700">Pros</div>
          <ul className="mt-2 space-y-1.5 text-[13.5px] text-foreground/80">
            {c.pros.map((p,i) => <li key={i} className="flex gap-2"><span className="text-emerald-700">✓</span>{p}</li>)}
          </ul>
        </div>
        <div>
          <div className="text-[11px] mono uppercase tracking-[0.18em] text-rose-700">Cons</div>
          <ul className="mt-2 space-y-1.5 text-[13.5px] text-foreground/80">
            {c.cons.map((p,i) => <li key={i} className="flex gap-2"><span className="text-rose-700">✗</span>{p}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ResearchTable() {
  const rows = [
    { feat: "Split ACH / Wire / Intl",          mer: "✅ tabs",       str: "✅ split cards", brx: "partial",         wse: "✅",               chs: "partial",  vrq: "❌" },
    { feat: "Bank name + bank address clear",    mer: "✅",            str: "✅",            brx: "✅",               wse: "✅",               chs: "✅",       vrq: "stored only" },
    { feat: "Beneficiary name + address clear",  mer: "✅",            str: "✅",            brx: "✅",               wse: "✅",               chs: "✅",       vrq: "stored only" },
    { feat: "Routing/Account reveal-gated",      mer: "in-session",    str: "in-session",    brx: "in-session",       wse: "in-session",       chs: "✅ MFA",   vrq: "✅ MFA" },
    { feat: "Per-field Copy button",             mer: "✅",            str: "✅",            brx: "✅",               wse: "✅",               chs: "✅",       vrq: "❌" },
    { feat: "Download PDF one-pager",            mer: "✅ wire PDF",   str: "partial",       brx: "❌",               wse: "✅",               chs: "✅ voided check", vrq: "❌" },
    { feat: "Cutoff times + ETA",                mer: "✅",            str: "✅",            brx: "✅",               wse: "partial",          chs: "✅",       vrq: "❌" },
  ];
  return (
    <div className="mt-5 rounded-2xl bg-white border border-border overflow-x-auto max-w-5xl">
      <table className="w-full text-[12.5px] min-w-[800px]">
        <thead className="bg-secondary/50 text-left text-[10.5px] uppercase tracking-widest text-foreground/55">
          <tr>
            <th className="px-4 py-2.5 font-medium">Feature</th>
            <th className="px-3 py-2.5 font-medium">Mercury</th>
            <th className="px-3 py-2.5 font-medium">Stripe Treasury</th>
            <th className="px-3 py-2.5 font-medium">Brex</th>
            <th className="px-3 py-2.5 font-medium">Wise</th>
            <th className="px-3 py-2.5 font-medium">Chase</th>
            <th className="px-3 py-2.5 font-medium bg-accent/30">Verqo today</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r,i) => (
            <tr key={i}>
              <td className="px-4 py-2.5 font-medium">{r.feat}</td>
              <td className="px-3 py-2.5 text-foreground/75">{r.mer}</td>
              <td className="px-3 py-2.5 text-foreground/75">{r.str}</td>
              <td className="px-3 py-2.5 text-foreground/75">{r.brx}</td>
              <td className="px-3 py-2.5 text-foreground/75">{r.wse}</td>
              <td className="px-3 py-2.5 text-foreground/75">{r.chs}</td>
              <td className={`px-3 py-2.5 bg-accent/15 ${r.vrq.startsWith("✅") ? "text-emerald-700" : r.vrq === "❌" ? "text-rose-700" : "text-amber-700"}`}>{r.vrq}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GapTable() {
  const rows = [
    { area: "payment_methods.bank_address_line1/2, bank_city/state/postal/country", ok: true,  note: "Collected in SettingsPanels.jsx + persisted" },
    { area: "payment_methods.account_holder + account_holder_address_*",            ok: true,  note: "Same — stored, not rendered" },
    { area: "Sensitive numbers (routing/account/IBAN/SWIFT)",                       ok: true,  note: "Stored + reveal-gated via POST /payment-methods/{id}/reveal" },
    { area: "GET /payment-methods passes addresses through (sensitive stripped)",   ok: true,  note: "Projection only strips highly sensitive fields" },
    { area: "RevealableNumber component (Mercury-style password reveal)",           ok: true,  note: "Already shipped — 30s auto-hide, audit-logged" },
    { area: "Render the address/holder fields in the wallet UI",                    ok: false, note: "MISSING — this is the build" },
    { area: "Per-field Copy buttons",                                               ok: false, note: "MISSING" },
    { area: "Downloadable PDF instructions one-pager",                              ok: false, note: "MISSING" },
    { area: "ACH / Wire split + cutoff timing display",                             ok: false, note: "MISSING" },
  ];
  return (
    <div className="mt-5 rounded-2xl bg-white border border-border overflow-hidden max-w-5xl">
      <table className="w-full text-[12.5px]">
        <thead className="bg-secondary/50 text-left text-[10.5px] uppercase tracking-widest text-foreground/55">
          <tr>
            <th className="px-4 py-2.5 w-12"/>
            <th className="px-4 py-2.5 font-medium">Area</th>
            <th className="px-4 py-2.5 font-medium">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r,i) => (
            <tr key={i}>
              <td className="px-4 py-2.5">{r.ok
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600"/>
                : <span className="inline-grid place-items-center w-4 h-4 rounded-full bg-rose-100 text-rose-700 text-[10px]">!</span>}</td>
              <td className="px-4 py-2.5">{r.area}</td>
              <td className="px-4 py-2.5 text-foreground/65">{r.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlanGrid() {
  return (
    <div className="mt-5 grid lg:grid-cols-3 gap-4">
      <PlanCol
        kind="ADD" tone="emerald"
        items={[
          { f: "/app/frontend/src/components/BankInstructionsCard.jsx", note: "Reusable card · ACH+Wire+Intl panels · CopyableField · CopyAllButton · PDF/Email CTAs (~250 lines)" },
          { f: "/app/backend/routes/wallet_instructions.py",            note: "GET /api/payment-methods/{id}/instructions.pdf · reportlab PDF · masked by default · reveal-token unlocks full · audit-logged (~150 lines)" },
        ]}
      />
      <PlanCol
        kind="MODIFY" tone="amber"
        items={[
          { f: "/app/frontend/src/pages/seller/SellerWallet.jsx",   note: "Swap SellerBankRow's RevealableNumber grid for <BankInstructionsCard/>" },
          { f: "/app/frontend/src/pages/buyer/BuyerPayments.jsx",   note: "Same swap inside MethodRow" },
          { f: "/app/backend/server.py",                            note: "1 line: app.include_router(wallet_instructions.router)" },
          { f: "/app/frontend/src/components/SettingsPanels.jsx",   note: 'Add hint "Required so we can render your ACH/Wire one-pager"' },
        ]}
      />
      <PlanCol
        kind="DELETE / NO-TOUCH" tone="rose"
        items={[
          { f: "Nothing deleted",                          note: "Backwards-compatible — older Plaid-imported methods show an inline 'Add bank address' CTA" },
          { f: "payment_methods schema",                   note: "Already has every field we need" },
          { f: "POST /payment-methods/{id}/reveal",        note: "Already perfect" },
          { f: "Auth, Plaid, Modern Treasury, Stripe",     note: "Untouched" },
        ]}
      />
    </div>
  );
}

function PlanCol({ kind, tone, items }) {
  const toneCls = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
  }[tone];
  return (
    <div className="rounded-2xl bg-white border border-border overflow-hidden">
      <div className={`${toneCls} border-b px-4 py-2 text-[10.5px] mono uppercase tracking-[0.22em]`}>{kind}</div>
      <div className="divide-y divide-border">
        {items.map((it,i) => (
          <div key={i} className="p-4">
            <div className="mono text-[11.5px] text-foreground/85 break-all">{it.f}</div>
            <div className="text-[12px] text-foreground/60 mt-1.5 leading-relaxed">{it.note}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
